import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { userSlice, userActions } from './store/userSlice';
import { projectSlice, projectActions } from './store/projectSlice';
import { uiSlice, uiActions } from './store/uiSlice';
import { photoSlice, photoActions } from './store/photoSlice';
import { cameraSettingsSlice, cameraSettingsActions } from './store/cameraSettingsSlice';
import { annotationSlice, annotationActions } from './store/annotationSlice';
import { settingsSlice, settingsActions } from './store/settingsSlice';
import { orgSlice, orgActions } from './store/orgSlice';
import { subscriptionSlice, subscriptionActions } from './store/subscriptionSlice';
import { AppState } from '@/types/store';
import type { User } from '@/types/index';
import { logger } from '@/utils/logger';
import { safeStorage } from '@/utils/safeStorage';

/**
 * Composed application store.
 *
 * The store is assembled from per-domain slices: each slice contributes its
 * initial state object plus an action factory `(set, get) => ({...})`. State
 * spreads come first, then actions, so action keys win on any collision.
 *
 * The `immer` middleware lets actions mutate draft state directly. State and
 * action types are aggregated in `@/types/store` (`AppState`); slice action
 * factories deliberately use loose `set`/`get` types internally to avoid the
 * circular reference that fully-typed Zustand + immer would create.
 */
const useStore = create<AppState>()(
  immer((set, get) => ({
    ...userSlice,
    ...projectSlice,
    ...uiSlice,
    ...photoSlice,
    ...cameraSettingsSlice,
    ...annotationSlice,
    ...settingsSlice,
    ...orgSlice,
    ...subscriptionSlice,
    ...userActions(set, get),
    ...projectActions(set, get),
    ...uiActions(set, get),
    ...photoActions(set, get),
    ...cameraSettingsActions(set),
    ...annotationActions(set, get),
    ...settingsActions(set, get),
    ...orgActions(set, get),
    ...subscriptionActions(set, get),
  })),
);

/** Shape of the persisted user record stored under the `user_data` key. */
interface StoredUser {
  id?: number | string;
  email?: string;
  username?: string;
  [key: string]: unknown;
}

/**
 * Initialize authentication state from storage on app start.
 *
 * Uses safeStorage (iOS Private Mode compatible). Restores token + user, then
 * kicks off a fire-and-forget boot validation of the stored token.
 */
const initializeAuth = (): void => {
  try {
    const token = safeStorage.getItem('auth_token') || safeStorage.getItem('token');
    const userData = safeStorage.getItem('user_data');

    if (token && userData) {
      // The persisted record is the full `User` object; type it loosely for
      // safe optional-field access but pass it through as `User` to `login`.
      const user = JSON.parse(userData) as StoredUser;
      logger.info('Restoring authentication from storage', {
        userId: user?.id,
        email: user?.email,
      });

      // Restore authentication state
      useStore.getState().setToken(token);
      useStore.getState().login(user.email || user.username || '', token, user as unknown as User);

      // Boot-validate the restored token. Without this check, a token that
      // was valid when last used but is now rejected by the server (server
      // restarted with a new JWT_SECRET, user row deleted, account
      // deactivated, etc.) leaves the client stuck in an "authenticated"
      // state with empty data — the user sees a half-rendered camera
      // screen instead of the login form. The 401/403 interceptor
      // (mobileApiService) handles auth-failure responses by wiping
      // storage and reloading; here we only need to cover the 404 case
      // that /auth/me uses for "user row not found", since that one
      // doesn't go through the auth middleware.
      validateStoredToken().catch((e: unknown) => {
        // validateStoredToken guards internally, but a dynamic-import failure
        // could still reject here — never let it become an unhandled rejection.
        logger.warn('Boot token validation threw', {
          error: e instanceof Error ? e.message : String(e),
        });
      });
    } else {
      logger.debug('No stored authentication found');
    }
  } catch (error: unknown) {
    logger.error('Error initializing authentication', {
      error: error instanceof Error ? error.message : String(error),
    });
    // Clear corrupted data
    safeStorage.removeItem('auth_token');
    safeStorage.removeItem('token');
    safeStorage.removeItem('user_data');
  }
};

/** Error shape thrown by mobileApiService — carries an HTTP-ish `status`. */
interface ApiError {
  status?: number;
  message?: string;
}

/**
 * Fire-and-forget ping. Lazy-imports mobileApiService to avoid the circular
 * dep (store ↔ apiService) and to keep the initial bundle clean.
 */
const validateStoredToken = async (): Promise<void> => {
  try {
    const { default: mobileApiService } = await import('@/services/api/mobileApiService');
    await mobileApiService.get('/auth/me');
    logger.debug('Boot-validate: stored token accepted by server');
  } catch (err: unknown) {
    const status = (err as ApiError | null)?.status;
    const message = (err as ApiError | null)?.message;
    // 401 + 403 (with auth-failure code) are already handled by the
    // response interceptor — it wipes storage and reloads, so this branch
    // never fires in those cases. 404 from /auth/me means the user row
    // is gone server-side; do the cleanup ourselves.
    if (status === 404) {
      logger.warn('Boot-validate: /auth/me 404, clearing zombie auth state');
      useStore.getState().logout();
    } else if (status === 0 || (typeof status === 'number' && status >= 500)) {
      logger.warn('Boot-validate: transient failure, keeping local auth state', { status });
    } else {
      logger.warn('Boot-validate: unexpected response', { status, message });
    }
  }
};

// Initialize auth when the module loads
if (typeof window !== 'undefined') {
  initializeAuth();
  // Dev diagnostic: expose the store on window so DevTools console can read
  // the current auth/screen state without React DevTools. Read-only intent.
  if (import.meta.env.DEV) {
    (window as unknown as { __PINCAM_STORE__: typeof useStore }).__PINCAM_STORE__ = useStore;
  }
}

export default useStore;
